import {
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useState } from 'react'
import {
  useConfiguration,
  useRemoveTaxRateGroup,
  useSaveConfiguration,
  useSaveTaxRateGroup,
  useTaxRateGroups,
  type TaxRateGroup,
} from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner } from '@/components/common'

const PER_TAX_RATE = 'billing.invoice.per.tax.rate'
const AMENITIES_SEPARATE = 'billing.amenities.separate.invoice'
const AUTO_NOTIFY_CUSTOMERS = 'notification.auto.customer'
const INVOICE_RATE_EDIT = 'billing.invoice.rate.edit'

/**
 * How a final bill is cut, and which rates travel together on it.
 *
 * <p>Catering is supplied at five and the things around it - the hall's power,
 * its cleaning, a projector hired for the evening - at eighteen. An office that
 * files and an accounts team that claims input credit both work a rate at a
 * time, so the bill goes out a rate at a time unless somebody says otherwise;
 * grouping decides how finely.</p>
 */
export function BillingTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const writable = canWrite('config')

  const { data: settings, error: settingsError } = useConfiguration()
  const saveSetting = useSaveConfiguration()
  const { data: groups, error } = useTaxRateGroups({ includeInactive: true })
  const save = useSaveTaxRateGroup()
  const remove = useRemoveTaxRateGroup()

  const [name, setName] = useState('')
  const [rates, setRates] = useState('')

  /** What is set, or what the server does when nothing is. */
  const setting = (key: string, fallback: boolean) => {
    const held = (settings ?? []).find((one) => one.key === key)
    return held ? held.value === 'true' : fallback
  }

  const set = (key: string, on: boolean) =>
    saveSetting.mutate({ key, value: String(on), valueType: 'BOOLEAN' })

  /** "5, 12" and "5 12" both mean the same two rates. */
  const readRates = (typed: string) =>
    typed
      .split(/[^0-9.]+/)
      .map((part) => Number(part))
      .filter((rate) => Number.isFinite(rate) && rate >= 0)

  const add = () => {
    const wanted = readRates(rates)
    if (!name.trim() || wanted.length === 0) return
    save.mutate(
      { body: { name: name.trim(), rates: wanted, sortOrder: 99 } },
      {
        onSuccess: () => {
          setName('')
          setRates('')
        },
      },
    )
  }

  const perRate = setting(PER_TAX_RATE, true)
  const autoNotify = setting(AUTO_NOTIFY_CUSTOMERS, true)
  const rateEditAllowed = setting(INVOICE_RATE_EDIT, true)

  return (
    <>
      <Typography variant="subtitle2">Automatic messages to customers</Typography>
      <Typography variant="caption" color="text.secondary">
        Whether a quotation, an order confirmation, a receipt and the rest reach a customer on their
        own, the moment each is raised. Off, nothing automatic ever goes out — every document is sent
        by hand, from the document&apos;s own screen, which this switch never touches.
      </Typography>

      <ErrorBanner error={settingsError ?? saveSetting.error} />

      <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 2 }}>
        <Switch
          checked={autoNotify}
          disabled={!writable || saveSetting.isPending}
          onChange={(event) => set(AUTO_NOTIFY_CUSTOMERS, event.target.checked)}
          inputProps={{ 'aria-label': 'Send automatic customer messages' }}
        />
        <Typography variant="body2">
          {autoNotify
            ? 'Sent automatically — WhatsApp and email go out on their own'
            : 'Off — every document is sent by hand'}
        </Typography>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2">Rate edit on an invoice</Typography>
      <Typography variant="caption" color="text.secondary">
        Whether a line&apos;s rate may be changed once the invoice carrying it already exists. Raising
        a counter sale is still typing the price actually charged — this only holds a correction to
        what was first saved.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 2 }}>
        <Switch
          checked={rateEditAllowed}
          disabled={!writable || saveSetting.isPending}
          onChange={(event) => set(INVOICE_RATE_EDIT, event.target.checked)}
          inputProps={{ 'aria-label': 'Allow a line rate to be edited after an invoice exists' }}
        />
        <Typography variant="body2">
          {rateEditAllowed
            ? 'Allowed — a rate may be corrected after the fact'
            : 'Off — a rate is fixed once the invoice is saved'}
        </Typography>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2">How the final bill is cut</Typography>
      <Typography variant="caption" color="text.secondary">
        A bill carrying two tax rates is worked apart by hand at both ends. Split, each rate is a
        document of its own with its own number; on one bill, the tax summary at the foot says what
        each rate came to.
      </Typography>

      <Stack spacing={1} sx={{ my: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={perRate}
            disabled={!writable || saveSetting.isPending}
            onChange={(event) => set(PER_TAX_RATE, event.target.checked)}
            inputProps={{ 'aria-label': 'One bill per tax rate' }}
          />
          <Typography variant="body2">
            {perRate ? 'A bill for each tax rate' : 'Every rate on one bill'}
          </Typography>
        </Stack>
        {/* Only asked where the rates are not already being split: the older
            separation of the venue's charges from the food is one rate band by
            another name, and offering both at once invites the question of
            which of them wins. */}
        {perRate ? null : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={setting(AMENITIES_SEPARATE, false)}
              disabled={!writable || saveSetting.isPending}
              onChange={(event) => set(AMENITIES_SEPARATE, event.target.checked)}
              inputProps={{ 'aria-label': 'Bill amenities separately' }}
            />
            <Typography variant="body2">
              The venue&apos;s own charges on a bill of their own — electricity, hall cleaning,
              recoveries
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2">Rates billed together</Typography>
      <Typography variant="caption" color="text.secondary">
        Group the rates that belong on one document and the split follows the groups — five and
        twelve under &ldquo;Catering&rdquo;, eighteen under &ldquo;Venue and services&rdquo;. A rate
        in no group is a document of its own, which is where every install starts.
      </Typography>

      <ErrorBanner error={error ?? save.error ?? remove.error} />

      {writable ? (
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ my: 2 }}>
          <TextField
            size="small"
            label="Group"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Catering"
            inputProps={{ maxLength: 60 }}
            sx={{ width: 220 }}
          />
          <TextField
            size="small"
            label="Rates"
            value={rates}
            onChange={(event) => setRates(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') add()
            }}
            placeholder="5, 12"
            helperText="Separated however you like"
            sx={{ width: 220 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!name.trim() || readRates(rates).length === 0 || save.isPending}
            onClick={add}
            sx={{ mt: 0.5 }}
          >
            Add
          </Button>
        </Stack>
      ) : null}

      {(groups ?? []).length === 0 ? (
        <EmptyState message="No rates are grouped, so each is billed on a document of its own." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={240}>Group</TableCell>
              <TableCell>Rates on one document</TableCell>
              <TableCell width={110} align="center">
                In use
              </TableCell>
              <TableCell width={60} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(groups ?? []).map((group: TaxRateGroup) => (
              <TableRow key={group.id} hover>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    defaultValue={group.name}
                    disabled={!writable}
                    inputProps={{ 'aria-label': `Name of the ${group.name} group` }}
                    onBlur={(event) => {
                      if (event.target.value.trim() && event.target.value !== group.name) {
                        save.mutate({
                          id: group.id,
                          body: {
                            name: event.target.value.trim(),
                            rates: group.rates,
                            sortOrder: group.sortOrder,
                            active: group.active,
                          },
                        })
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {group.rates.map((rate) => (
                      <Chip key={rate} size="small" label={`${rate}%`} />
                    ))}
                    <TextField
                      size="small"
                      variant="standard"
                      defaultValue=""
                      disabled={!writable}
                      placeholder="Set the rates"
                      inputProps={{ 'aria-label': `Rates under ${group.name}` }}
                      onBlur={(event) => {
                        const wanted = readRates(event.target.value)
                        if (wanted.length === 0) return
                        save.mutate({
                          id: group.id,
                          body: {
                            name: group.name,
                            rates: wanted,
                            sortOrder: group.sortOrder,
                            active: group.active,
                          },
                        })
                        event.target.value = ''
                      }}
                      sx={{ width: 140 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={group.active}
                    disabled={!writable}
                    onChange={(event) =>
                      save.mutate({
                        id: group.id,
                        body: {
                          name: group.name,
                          rates: group.rates,
                          sortOrder: group.sortOrder,
                          active: event.target.checked,
                        },
                      })
                    }
                    inputProps={{ 'aria-label': `Use the ${group.name} group` }}
                  />
                </TableCell>
                <TableCell align="right">
                  {writable ? (
                    <Tooltip title="Ungroup these rates. Bills already raised are untouched.">
                      <IconButton
                        size="small"
                        onClick={() => remove.mutate(group.id)}
                        aria-label={`Remove the ${group.name} group`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
